/**
 * Agent run service — talks to the merged streaming endpoint
 * `POST /chat/runs/<client_id>`.
 *
 * One request does everything: it starts the run and the response body IS
 * the SSE stream. (The old start → subscribe two-step kept run state in
 * backend process memory, which broke with multiple uvicorn workers.)
 *
 * Why fetch instead of EventSource:
 *   EventSource cannot send custom headers or use POST, but we need to
 *   forward the Okta `Authorization: Bearer <token>` header and a JSON
 *   body. fetch streaming gives us both; a tiny SSE parser (below) handles
 *   framing.
 *
 * Cancelling: abort the passed-in signal. The server treats the dropped
 * connection as a cancel request — there is no cancel endpoint.
 */

import { oktaAuth } from "../context/AuthProvider";
import { API_CHAT_RUNS } from "./constants";
import { getEnvValue } from "./getEnvValue";

const BASE_URL = getEnvValue("BACKEND_URL", '');

async function authHeaders(extra = {}) {
  const token = await oktaAuth.getOrRenewAccessToken();
  return {
    Authorization: token ? `Bearer ${token}` : undefined,
    ...extra,
  };
}

/**
 * Start a run and yield its parsed SSE events. The caller should pass an
 * AbortController.signal; aborting it cancels the run server-side.
 *
 * Yields envelopes of the shape: { event, data, id? } where `data` is the
 * already-parsed JSON object. The first event is `ready` with
 * `data.run_id`.
 */
export async function* streamRun(
  clientId,
  { conversationId, message },
  { signal } = {},
) {
  const res = await fetch(`${BASE_URL}${API_CHAT_RUNS}/${clientId}`, {
    method: "POST",
    headers: await authHeaders({
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    }),
    body: JSON.stringify({
      conversation_id: conversationId,
      message,
    }),
    signal,
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    throw new Error(`streamRun failed: ${res.status} ${text}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line.
      let sep;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const evt = parseSseFrame(raw);
        if (evt) yield evt;
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch (_e) {
      /* noop */
    }
  }
}

function parseSseFrame(raw) {
  let id;
  let event = "message";
  const dataLines = [];
  for (const line of raw.split("\n")) {
    if (!line || line.startsWith(":")) continue; // blank or comment (keep-alive)
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "id") id = value;
    else if (field === "event") event = value;
    else if (field === "data") dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  let data;
  try {
    data = JSON.parse(dataLines.join("\n"));
  } catch (_e) {
    data = { raw: dataLines.join("\n") };
  }
  return { id, event, data };
}
